/**
 * ============================================================================
 * THE SHOWROOM v3.5 — PAIRING VIEW
 * In-Place Cross-Collection Pairing Display
 * ============================================================================
 *
 * FILE: showroom-pairing-view.js (standalone)
 *
 * Replaces the old "Explore Pairings" behavior that navigated away to another
 * collection toggle. Now shows paired products in-place, same as the Departure
 * Lounge experience card → products view pattern.
 *
 * INSTALLATION:
 * 1. Upload to Shopify Assets
 * 2. Add to the-showroom.liquid AFTER the main showroom JS:
 *    <script src="{{ 'showroom-pairing-view.js' | asset_url }}" defer></script>
 *
 * 3. Add ONE edit to showroom-v3_5.js — in the category button click handler,
 *    alongside the existing resetDepartureState() call:
 *
 *      resetPairingView();
 *
 * 4. REMOVE the old Explore Pairings click handler from showroom-grid-products.liquid
 *    (lines ~1789–1857). Keep the populatePairingLists() function and the
 *    banner auto-clear listener — only remove the click handler that does
 *    categoryBtn.click() navigation.
 *
 * ============================================================================
 */

(function() {
  'use strict';

  /* ========================================================================
     STATE
     ======================================================================== */

  window.pairingViewState = {
    active: false,
    sourceCardEl: null,         // The product card that triggered the pairing view
    sourceCardName: '',         // Title of the source product
    sourceCollection: '',       // Which collection we came from
    linkedHandles: [],          // Paired product handles
    targetCollection: null,     // Which collection to pull pairings from (null = all)
    originalOrder: null         // Snapshot of grid child order before rearranging
  };


  /* ========================================================================
     RENDER PAIRING VIEW — show source card + paired products in-place
     ======================================================================== */

  function renderPairingView() {
    var productGrid = document.getElementById('productGrid');
    if (!productGrid) return;

    var state = pairingViewState;

    productGrid.classList.add('fading');

    setTimeout(function() {
      // Snapshot original DOM order before first rearrange
      if (!state.originalOrder) {
        state.originalOrder = Array.from(productGrid.children);
      }

      var handleSet = {};
      state.linkedHandles.forEach(function(h) {
        handleSet[h.trim().toLowerCase()] = true;
      });

      var visibleCount = 0;
      var visibleProducts = [];
      var allCards = document.querySelectorAll('.product-card');

      allCards.forEach(function(card) {
        if (card.classList.contains('cart-view-card')) return;

        // Source card stays visible
        if (card === state.sourceCardEl) {
          card.classList.remove('hidden');
          return;
        }

        // Departure cards always hidden in pairing view
        if (card.classList.contains('departure-card')) {
          card.classList.add('hidden');
          return;
        }

        // Cross-collection handle match
        var cardHandle = (card.dataset.productHandle || '').toLowerCase();
        var cardCollection = card.dataset.collection || '';

        // If a target collection is specified, only show pairings from that collection
        var collectionMatch = !state.targetCollection || cardCollection === state.targetCollection;

        if (cardHandle && handleSet[cardHandle] && collectionMatch) {
          card.classList.remove('hidden');
          visibleProducts.push(card);
          visibleCount++;
        } else {
          card.classList.add('hidden');
        }
      });

      // Sort paired products alphabetically
      visibleProducts.sort(function(a, b) {
        var tA = (a.querySelector('.product-card-header-title') || {}).textContent || '';
        var tB = (b.querySelector('.product-card-header-title') || {}).textContent || '';
        return tA.toLowerCase().localeCompare(tB.toLowerCase());
      });

      // Pin source card first, then sorted paired products
      if (state.sourceCardEl) productGrid.appendChild(state.sourceCardEl);
      visibleProducts.forEach(function(card) { productGrid.appendChild(card); });

      // Show pairing banner
      showPairingBanner(state.sourceCardName, visibleCount);

      productGrid.classList.remove('fading');

      // Re-init carousels
      if (typeof initializeCarousels === 'function') initializeCarousels();

      // Scroll to top of grid
      productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }


  /* ========================================================================
     EXIT PAIRING VIEW — restore grid to pre-pairing state
     ======================================================================== */

  function exitPairingView() {
    var productGrid = document.getElementById('productGrid');
    if (!productGrid) return;

    var state = pairingViewState;

    // Restore original DOM order
    if (state.originalOrder) {
      state.originalOrder.forEach(function(child) {
        productGrid.appendChild(child);
      });
    }

    // Clear state
    state.active = false;
    state.sourceCardEl = null;
    state.sourceCardName = '';
    state.sourceCollection = '';
    state.linkedHandles = [];
    state.targetCollection = null;
    state.originalOrder = null;

    // Remove pairing banner
    removePairingBanner();

    // Re-trigger the active collection — its own fade handles the transition
    var activeBtn = document.querySelector('.category-btn.active');
    if (activeBtn) {
      activeBtn.click();
    }
  }


  /* ========================================================================
     PAIRING BANNER — shows above grid with back button + context
     ======================================================================== */

  function showPairingBanner(productName, count) {
    removePairingBanner();

    var banner = document.createElement('div');
    banner.className = 'pair-filter-banner';
    banner.id = 'pairingViewBanner';
    banner.innerHTML =
      '<button class="pair-filter-clear-btn" id="pairingViewBackBtn">← Back</button>' +
      '<span class="pair-filter-banner-text">' +
        'Showing <strong>' + count + ' pairing' + (count !== 1 ? 's' : '') + '</strong> for ' +
        escapeHtml(productName) +
      '</span>';

    var grid = document.getElementById('productGrid');
    if (grid && grid.parentNode) {
      grid.parentNode.insertBefore(banner, grid);
    }
  }

  function removePairingBanner() {
    var existing = document.getElementById('pairingViewBanner');
    if (existing) existing.remove();
  }


  /* ========================================================================
     EVENT: "Explore Pairings" button click — INTERCEPT
     Captures clicks on .pair-explore-btn BEFORE the old handler.
     ======================================================================== */

  document.addEventListener('click', function(e) {
    var pairBtn = e.target.closest('.pair-explore-btn');
    if (!pairBtn) return;

    e.stopPropagation();
    e.stopImmediatePropagation();
    e.preventDefault();

    var card = pairBtn.closest('.product-card');
    if (!card) return;

    var allHandles = (card.dataset.pairingHandles || '').split(',')
      .map(function(h) { return h.trim(); })
      .filter(Boolean);

    if (allHandles.length === 0) return;

    var titleEl = card.querySelector('.product-card-header-title');
    var productName = titleEl ? titleEl.textContent.trim() : 'this product';
    var targetCollection = pairBtn.dataset.navigateCollection || null;

    // Set state
    pairingViewState.active = true;
    pairingViewState.sourceCardEl = card;
    pairingViewState.sourceCardName = productName;
    pairingViewState.sourceCollection = card.dataset.collection || '';
    pairingViewState.linkedHandles = allHandles;
    pairingViewState.targetCollection = targetCollection;

    // Inflate any deferred collections that contain paired products
    if (typeof inflateCollection === 'function') {
      var jsonEl = document.getElementById('pairingHandleMap');
      if (jsonEl) {
        try {
          var map = JSON.parse(jsonEl.textContent);
          var inflatedSet = {};
          allHandles.forEach(function(h) {
            var entry = map[h.trim()];
            if (entry && entry.collection && !inflatedSet[entry.collection]) {
              inflatedSet[entry.collection] = true;
              inflateCollection(entry.collection);
            }
          });
        } catch (e) { /* JSON already parsed fine earlier, this won't fail */ }
      }
    }

    // Render in-place
    renderPairingView();

    // Analytics
    if (typeof trackShowroom === 'function') {
      trackShowroom('pairing_view_opened', {
        source_product: productName,
        source_collection: card.dataset.collection,
        target_collection: targetCollection,
        pairing_count: allHandles.length
      });
    }
  }, true);  // CAPTURE phase — fires before any other click handlers


  /* ========================================================================
     EVENT: Back button in pairing banner
     ======================================================================== */

  document.addEventListener('click', function(e) {
    if (!e.target.closest('#pairingViewBackBtn')) return;
    e.stopPropagation();

    exitPairingView();

    if (typeof trackShowroom === 'function') {
      trackShowroom('pairing_view_closed', {});
    }
  });


  /* ========================================================================
     RESET — called when switching toggles (from showroom-v3_5.js)
     ======================================================================== */

  window.resetPairingView = function() {
    if (!pairingViewState.active) return;

    var productGrid = document.getElementById('productGrid');

    // Restore original grid order
    if (pairingViewState.originalOrder && productGrid) {
      pairingViewState.originalOrder.forEach(function(child) {
        productGrid.appendChild(child);
      });
    }

    // Clear state
    pairingViewState.active = false;
    pairingViewState.sourceCardEl = null;
    pairingViewState.sourceCardName = '';
    pairingViewState.sourceCollection = '';
    pairingViewState.linkedHandles = [];
    pairingViewState.targetCollection = null;
    pairingViewState.originalOrder = null;

    // Remove banner
    removePairingBanner();
  };


  /* ========================================================================
     UTILITY
     ======================================================================== */

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
  }

})();